import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useSubscription } from '@/contexts/SubscriptionContext';

export type UsageType = 'voice' | 'chat' | 'situation';

interface DailyUsage {
  voice_conversations: number;
  text_chats: number;
  situations_created: number;
  api_minutes: number;
}

interface TierLimits {
  voice_conversations: number;
  text_chats: number;
  max_situations: number;
  monthly_api_minutes: number;
}

type CheckCanUseResult = {
  canUse: boolean;
  remaining: number;
  used: number;
  limit: number;
  tier: string;
};

function parseCheckCanUse(data: unknown): CheckCanUseResult {
  if (!data || typeof data !== "object") {
    return { canUse: false, remaining: 0, used: 0, limit: 0, tier: "unknown" };
  }
  const row = data as Record<string, unknown>;
  return {
    canUse: typeof row.canUse === "boolean" ? row.canUse : false,
    remaining: typeof row.remaining === "number" ? row.remaining : 0,
    used: typeof row.used === "number" ? row.used : 0,
    limit: typeof row.limit === "number" ? row.limit : 0,
    tier: typeof row.tier === "string" ? row.tier : "unknown",
  };
}

export function useUsageTracker() {
  const { subscription, isPaid, loading: subscriptionLoading } = useSubscription();
  const [usage, setUsage] = useState<DailyUsage>({
    voice_conversations: 0,
    text_chats: 0,
    situations_created: 0,
    api_minutes: 0,
  });
  const [monthlyMinutes, setMonthlyMinutes] = useState({ used: 0, limit: 0, remaining: 0 });
  const [loading, setLoading] = useState(true);

  const limits: TierLimits = {
    voice_conversations: subscription?.limits.daily_voice_conversations ?? 0,
    text_chats: subscription?.limits.daily_text_chats ?? 0,
    max_situations: subscription?.limits.max_situations ?? 0,
    monthly_api_minutes: subscription?.limits.monthly_api_minutes ?? 0,
  };

  useEffect(() => {
    if (!subscriptionLoading) {
      void loadUsage();
    }
  }, [subscriptionLoading]);

  const loadUsage = async () => {
    try {
      const [{ data, error }, monthly] = await Promise.all([
        supabase.rpc('get_daily_usage'),
        supabase.rpc('get_monthly_api_usage'),
      ]);
      
      if (error) {
        console.error('Error loading usage:', error);
      } else if (data && data.length > 0) {
        setUsage({
          voice_conversations: data[0].voice_conversations || 0,
          text_chats: data[0].text_chats || 0,
          situations_created: data[0].situations_created || 0,
          api_minutes: data[0].api_minutes || 0,
        });
      }

      if (!monthly.error && monthly.data && typeof monthly.data === 'object') {
        const row = monthly.data as { used?: number; limit?: number; remaining?: number };
        setMonthlyMinutes({
          used: row.used ?? 0,
          limit: row.limit ?? 0,
          remaining: row.remaining ?? 0,
        });
      }
    } catch (err) {
      console.error('Error in loadUsage:', err);
    } finally {
      setLoading(false);
    }
  };

  const canUse = useCallback((_type: UsageType): boolean => {
    return isPaid;
  }, [isPaid]);

  const getRemainingUses = useCallback((_type: UsageType): number => {
    return isPaid ? -1 : 0;
  }, [isPaid]);

  const incrementUsage = useCallback(async (type: UsageType): Promise<void> => {
    try {
      const { error } = await supabase.rpc('increment_usage', {
        usage_type: type,
      });

      if (error) {
        console.error('Error incrementing usage:', error);
        return;
      }

      setUsage(prev => ({
        ...prev,
        voice_conversations: type === 'voice' ? prev.voice_conversations + 1 : prev.voice_conversations,
        text_chats: type === 'chat' ? prev.text_chats + 1 : prev.text_chats,
        situations_created: type === 'situation' ? prev.situations_created + 1 : prev.situations_created,
        api_minutes: type === 'voice' ? prev.api_minutes + 1 : prev.api_minutes,
      }));
      if (type === 'voice') {
        setMonthlyMinutes((prev) => ({
          ...prev,
          used: prev.used + 1,
          remaining: prev.limit === -1 ? -1 : Math.max(0, prev.remaining - 1),
        }));
      }
    } catch (err) {
      console.error('Error in incrementUsage:', err);
    }
  }, []);

  const checkCanUseAsync = useCallback(async (type: UsageType): Promise<{
    canUse: boolean;
    remaining: number;
    used: number;
    limit: number;
    tier: string;
  }> => {
    try {
      const { data, error } = await supabase.rpc('check_can_use', {
        usage_type: type,
      });

      if (error) {
        console.error('Error checking usage:', error);
        return { canUse: false, remaining: 0, used: 0, limit: 0, tier: 'unknown' };
      }

      return parseCheckCanUse(data);
    } catch (err) {
      console.error('Error in checkCanUseAsync:', err);
      return { canUse: false, remaining: 0, used: 0, limit: 0, tier: 'unknown' };
    }
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    await loadUsage();
  }, []);

  return {
    canUse,
    getRemainingUses,
    incrementUsage,
    checkCanUseAsync,
    refresh,
    usage,
    limits,
    monthlyMinutes,
    loading: loading || subscriptionLoading,
  };
}
